import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { TotpService } from './totp.service'; import { SanctumService } from 'src/app/services/sanctum.service';
import { Preferences } from '@capacitor/preferences';

import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';

export type OsType =
  | 'android' | 'ios' | 'ipados'
  | 'windows' | 'macos' | 'linux' | 'chromeos'
  | 'unknown';

export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'unknown';

export interface ClientPlatformInfo {
  os: OsType;
  deviceType: DeviceType;
  isMobileWeb: boolean;   // mobile browser (não-nativo)
  isNative: boolean;      // Capacitor nativo
  ua: string;
}

export async function detectClientPlatform(): Promise<ClientPlatformInfo> {
  const ua = (navigator.userAgent || navigator.vendor || (window as any).opera || '').trim();
  const isTouch = matchMedia?.('(pointer: coarse)')?.matches || false;
  const maxTouch = (navigator as any).maxTouchPoints ?? 0;

  // 1) UA-CH (Chromium 89+). Nem todo site tem permissão a high-entropy;
  // para nosso caso basta platform+mobile.
  const uad = (navigator as any).userAgentData;
  const uadMobile: boolean | undefined = uad?.mobile;
  const uadPlatform: string | undefined = uad?.platform || uad?.brands?.[0]?.brand;

  const p = (uadPlatform || '').toLowerCase();

  // Heurística iOS / iPadOS (Safari mascara como "Mac" às vezes)
  const isiPadOS =
    (/ipad/i.test(ua) || (/macintosh/i.test(ua) && maxTouch > 1)) ? true : false;

  const isIOS =
    /iPhone|iPod/i.test(ua) ||
    (/iPad/i.test(ua) && !/Macintosh/i.test(ua)) ||
    (!!(navigator as any).standalone && /safari/i.test(ua) === false);

  // 2) OS
  let os: OsType = 'unknown';
  if (/android/i.test(ua) || p.includes('android')) os = 'android';
  else if (isiPadOS) os = 'ipados';
  else if (isIOS || /iOS/i.test(p)) os = 'ios';
  else if (/windows nt/i.test(ua) || p.includes('windows')) os = 'windows';
  else if (/mac os x|macintosh/i.test(ua) || p.includes('mac')) os = 'macos';
  else if (/cros/i.test(ua) || p.includes('chrome os')) os = 'chromeos';
  else if (/linux/i.test(ua) || p.includes('linux')) os = 'linux';

  // 3) Device type
  const isAndroidPhone = os === 'android' && /mobile/i.test(ua);
  const isAndroidTablet = os === 'android' && !/mobile/i.test(ua);
  const isIPhone = os === 'ios';
  const isIPad = os === 'ipados';

  let deviceType: DeviceType = 'desktop';
  if (isAndroidPhone || isIPhone) deviceType = 'mobile';
  else if (isAndroidTablet || isIPad) deviceType = 'tablet';
  else if (isTouch && (os === 'windows' || os === 'chromeos')) deviceType = 'tablet';

  const isMobileWeb = !!(uadMobile ?? (deviceType === 'mobile' || deviceType === 'tablet'));

  return {
    os,
    deviceType,
    isMobileWeb,
    isNative: false, // você sobrescreve com Capacitor abaixo
    ua,
  };
}


@Injectable({ providedIn: 'root' })
export class AuthHeaderService {
  constructor(private totpService: TotpService, private sanctum: SanctumService) {

  }


  async resolveAccessToken(externalToken?: string): Promise<string> {
    if (externalToken) return externalToken;

    // 1) Nativo (Capacitor)
    try {
      if (Capacitor.isNativePlatform()) {
        const r = await Preferences.get({ key: 'access_token' });
        if (r.value) return r.value;
      }
    } catch { }

    // 2) Web: sessionStorage primeiro
    try {
      const s = sessionStorage.getItem('access_token');
      if (s) return s;
    } catch { }

    // 3) Compat: localStorage (caso ainda exista em usuários antigos)
    try {
      const l = localStorage.getItem('access_token');
      if (l) return l;
    } catch { }

    return '';
  }

  async getAuthHeaders(externalToken?: string): Promise<HttpHeaders> {

    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform(); // 'android', 'ios', 'web'
    const token = externalToken || localStorage.getItem('access_token') || '';

    const headersConfig: { [key: string]: string } = {
      'Content-Type': 'application/json',
    };

    const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    // forceTokenAuth: ver comentário em environment.ts — liga o mesmo fluxo
    // de token que já usávamos em localhost, mas em qualquer domínio
    // marcado no environment (preview de teste com frontend/backend em
    // domínios não relacionados).
    const simulateNative = isLocalhost || environment.forceTokenAuth;

    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';


    ///////////////////////
    const isShopifyContext =
      typeof window !== 'undefined' &&
      (window.location.hostname.endsWith('.myshopify.com'));
    if (isShopifyContext) {
      headersConfig['Authorization'] = `Bearer ${token}`;
      headersConfig['X-Mobile-App'] = 'true';
    }
    ////////////////////
    // ---- NOVO: detecção robusta do SO / device (mobile web vs desktop)
    const info = await detectClientPlatform();
    // sobrescreve isNative se necessário
    const reallyNative = isNative || simulateNative;
    const isMobileWeb = info.isMobileWeb && platform === 'web' && !reallyNative;

    // Força header em rotas de OAuth (Android/iOS no navegador)
    const isOauthPath = [
      '/auth/youtube',
      '/auth/tiktok',
      '/auth/shopify',
      '/auth/ga-4',
      '/auth/instagram',
    ].some(p => currentPath.startsWith(p));

    const shouldAttachMobileAuth =
      reallyNative ||
      isShopifyContext ||
      (isMobileWeb && isOauthPath); // <- aqui entra Android/iOS via navegador

    //if (isNative || simulateNative) {
    if (isNative || simulateNative) {
      headersConfig['Authorization'] = `Bearer ${token}`;
      headersConfig['X-Mobile-App'] = 'true';

      const deviceHeaders = await this.getDeviceHeaders(); // você pode injetar o DeviceService
      Object.assign(headersConfig, deviceHeaders);

      try {
        const totpCode = await this.totpService.getCurrentCode();
        headersConfig['X-App-Code'] = totpCode;
      } catch (err) {
        console.warn('[AuthHeaderService] Erro ao gerar TOTP:', err);
      }

      return new HttpHeaders(headersConfig);
    }

    // Ambiente web (SPA)
    const xsrfToken = this.getCookie('XSRF-TOKEN');
    if (xsrfToken) {
      headersConfig['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
    }

    return new HttpHeaders(headersConfig);
  }


  async getHttpOptions(externalToken?: string): Promise<{ headers: HttpHeaders; withCredentials?: boolean }> {
    const headers = await this.getAuthHeaders(externalToken);
    const isNative = Capacitor.isNativePlatform();
    return {
      headers,
      ...(isNative ? {} : { withCredentials: true })
    };
  }

  // auth-header.service.ts (ou onde preferir)
  async getCreatorOptions(): Promise<{ headers: HttpHeaders; withCredentials?: boolean }> {
    // força renovar XSRF/CSRF no contexto creator
    await this.sanctum.initSanctum('creator');

    // reaproveita tudo que você já faz (Content-Type, XSRF padrão, mobile, etc.)
    const base = await this.getHttpOptions();

    // injeta o contexto e garante XSRF mais recente (sem atob)
    const headersObj: Record<string, string> = {
      ...Object.fromEntries((base.headers as any).headersMap || []), // fallback caso precise
      'X-Auth-Context': 'creator',
      'X-Requested-With': 'XMLHttpRequest'
    };

    const m = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
    if (m) headersObj['X-XSRF-TOKEN'] = decodeURIComponent(m[1]);

    return {
      ...base,
      headers: new HttpHeaders(headersObj),
      withCredentials: true, // força credenciais no SPA
    };
  }



  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null; // não decodifica aqui — deixa pro headers
  }


  public async getDeviceHeaders(): Promise<{ [key: string]: string }> {
    const headers: { [key: string]: string } = {};

    try {
      const info = await Device.getInfo();

      headers['X-Device-Name'] = info.model || 'Unknown';
      headers['X-Device-OS'] = info.operatingSystem || info.platform || 'unknown';

      const fcmResult = await Preferences.get({ key: 'fcm_token' });
      const fcmToken = fcmResult.value;

      if (typeof fcmToken === 'string' && fcmToken.length > 0) {
        headers['X-Device-Token'] = fcmToken;
      }

    } catch (err) {
      console.warn('[AuthHeaderService] Erro ao obter dados do dispositivo:', err);
    }

    return headers;
  }


  // auth-header.service.ts
  async getPublicHttpOptions(): Promise<{ headers: HttpHeaders; withCredentials: false }> {
    const headersConfig: Record<string, string> = {
      'Accept': 'application/json',
      'X-Full-Referer': window.location.href,
    };

    return {
      headers: new HttpHeaders(headersConfig),
      withCredentials: false,
    };
  }



  async getWebOnlyHeaders(): Promise<HttpHeaders> {
    const headersConfig: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
  
    const xsrfToken = this.getCookie('XSRF-TOKEN');
    if (xsrfToken) headersConfig['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
  
    // NÃO põe Authorization
    // NÃO põe X-Mobile-App
  
    return new HttpHeaders(headersConfig);
  }
  

}

